// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Resolving a pasted URL to a working provider.
 *
 * Known hosts map straight to their provider. Anything else is probed: a
 * self-hosted GitLab and a self-hosted Forgejo look identical from a URL, so
 * the only honest way to tell them apart is to ask each in turn.
 */

import { asRepoError, unreachable, unsupportedHost, RepoAccessError } from './errors.ts';
import { GiteaProvider, GitHubProvider, GitLabProvider } from './providers.ts';
import { parseRepoUrl, toSource, type ParsedUrl } from './url.ts';
import type { FetchLike, Provider, ProviderId, RepoSource } from './types.ts';

export * from './types.ts';
export * from './errors.ts';
export { parseRepoUrl, toSource } from './url.ts';
export type { ParsedUrl } from './url.ts';
export { GiteaProvider, GitHubProvider, GitLabProvider } from './providers.ts';

/**
 * The browser's `fetch`, locked down.
 *
 * `credentials: 'omit'` matters: without it the browser would attach cookies
 * for a self-hosted host the user names, sending their session to a server this
 * tool has no business authenticating against. `no-referrer` keeps the page
 * address out of the request too.
 */
export function browserFetch(url: string, init?: { headers?: Record<string, string> }): Promise<Response> {
  return fetch(url, {
    method: 'GET',
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    ...(init?.headers ? { headers: init.headers } : {}),
  });
}

export function makeProvider(source: RepoSource, fetchImpl: FetchLike): Provider {
  switch (source.provider) {
    case 'github':
      return new GitHubProvider(source, fetchImpl);
    case 'gitlab':
      return new GitLabProvider(source, fetchImpl);
    case 'gitea':
      return new GiteaProvider(source, fetchImpl);
  }
}

export interface ResolvedRepo {
  provider: Provider;
  parsed: ParsedUrl;
}

/**
 * Parse `input` and return a provider that has successfully answered for it.
 *
 * For an unknown host both GitLab and Gitea are tried. A CORS refusal and an
 * unreachable host are indistinguishable in a browser, so if *both* probes fail
 * that way the reachability error is reported rather than "unsupported" — the
 * host may well be supported and simply unreadable from a web page.
 */
export async function resolveRepo(input: string, fetchImpl: FetchLike): Promise<ResolvedRepo> {
  const parsed = parseRepoUrl(input);

  if (parsed.provider !== undefined) {
    const provider = makeProvider(toSource(parsed, parsed.provider), fetchImpl);
    await provider.info();
    return { provider, parsed };
  }

  const candidates: ProviderId[] = ['gitlab', 'gitea'];
  const failures: RepoAccessError[] = [];
  for (const id of candidates) {
    const provider = makeProvider(toSource(parsed, id), fetchImpl);
    try {
      await provider.info();
      return { provider, parsed };
    } catch (err) {
      failures.push(asRepoError(err, parsed.host, id));
    }
  }

  // Prefer a specific diagnosis over "we could not tell". A reachability
  // failure is reported without attributing it to whichever probe happened to
  // run first — the host was never identified, so the hint must cover both.
  if (failures.some((f) => f.kind === 'unreachable')) throw unreachable(parsed.host);
  const informative = failures.find((f) => f.kind !== 'not-found');
  if (informative) throw informative;
  throw unsupportedHost(parsed.host);
}
